// src/components/Testimonials.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HiStar } from 'react-icons/hi';

const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      position: "CTO",
      company: "TechCorp Solutions", // Keep company in data, but won't display
      rating: 5,
      text: "EFA Tech Solutions transformed our entire digital infrastructure. Their expertise in cloud solutions and mobile development exceeded our expectations. The team was professional, responsive, and delivered on time.",
      avatar: "/api/placeholder/80/80"
    },
    {
      id: 2,
      name: "Michael Chen",
      position: "CEO",
      rating: 5,
      text: "Working with EFA Tech Solutions was a game-changer for our startup. They built our MVP from scratch and helped us scale rapidly. Their AI solutions gave us a competitive edge in the market.",
      avatar: "/api/placeholder/80/80"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      position: "Director of IT",
      company: "Healthcare Plus",
      rating: 5,
      text: "The healthcare management system they developed for us has streamlined our operations significantly. The security features and user interface are exceptional. Highly recommend EFA Tech Solutions' services.",
      avatar: "/api/placeholder/80/80"
    },
    {
      id: 4,
      name: "David Thompson",
      position: "Founder",
      company: "EcoTech Solutions",
      rating: 5,
      text: "EFA Tech Solutions helped us build a robust e-commerce platform that handles thousands of transactions daily. Their attention to detail and post-launch support has been outstanding.",
      avatar: "/api/placeholder/80/80"
    }
  ];

  // Auto-advance testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change duration here (5000ms = 5 seconds)

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section id="testimonials" className="section-padding bg-gray-50 relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-32 -left-32 w-96 h-96 bg-accent-100 rounded-full opacity-25"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 0],
            x: [0, 70, 0],
            y: [0, -40, 0]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-32 -right-32 w-80 h-80 bg-primary-100 rounded-full opacity-30"
          animate={{
            scale: [1, 1.4, 1],
            rotate: [360, 180, 0],
            x: [0, -50, 0],
            y: [0, 60, 0]
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
        />
        <motion.div
          className="absolute top-1/4 right-1/3 w-64 h-64 bg-secondary-100 rounded-full opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 360, 0],
            x: [0, 30, 0],
            y: [0, -15, 0]
          }}
          transition={{
            duration: 26,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 6
          }}
        />

        {/* Testimonial-themed floating elements */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute opacity-30"
            style={{
              left: `${20 + i * 15}%`,
              top: `${25 + i * 8}%`,
            }}
            animate={{
              y: [-25, 25, -25],
              x: [-15, 15, -15],
              opacity: [0.2, 0.6, 0.2],
              rotate: [0, 360, 0]
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 1.2
            }}
          >
            <HiStar className="w-6 h-6 text-yellow-400" />
          </motion.div>
        ))}
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            whileInView={{
              scale: [0.9, 1.05, 1],
              opacity: [0, 1, 1]
            }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            What Our <span className="text-primary-600">Clients Say</span>
          </motion.h2>
          <motion.p
            variants={itemVariants}
            whileInView={{
              y: [30, 0],
              opacity: [0, 1]
            }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Don't just take our word for it. Here's what our satisfied clients have to say about EFA Tech Solutions.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-4xl mx-auto"
        >
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  scale: 1,
                  boxShadow: "0 25px 50px rgba(0, 82, 204, 0.15)"
                }}
                exit={{ opacity: 0, x: -100, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
              >
                {/* Enhanced Stars */}
                <div className="flex justify-center mb-6">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: i * 0.1, duration: 0.3 }}
                      whileHover={{ scale: 1.2, rotate: 360 }}
                    >
                      <HiStar className="w-6 h-6 text-yellow-400" />
                    </motion.div>
                  ))}
                </div>

                {/* Enhanced Testimonial Text */}
                <motion.blockquote
                  className="text-lg md:text-xl text-gray-700 text-center mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  "{testimonials[currentIndex].text}"
                </motion.blockquote>

                {/* Enhanced Client Info */}
                <motion.div
                  className="flex items-center justify-center space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center text-white font-bold text-xl"
                    whileHover={{
                      scale: 1.1,
                      boxShadow: "0 10px 25px rgba(0, 82, 204, 0.3)"
                    }}
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(0, 82, 204, 0.1)",
                        "0 0 0 20px rgba(0, 82, 204, 0)",
                      ]
                    }}
                    transition={{
                      boxShadow: {
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }
                    }}
                  >
                    {testimonials[currentIndex].name.charAt(0)}
                  </motion.div>
                  <div className="text-left">
                    <motion.div
                      className="font-semibold text-gray-900"
                      whileHover={{ scale: 1.05, color: "#0052CC" }}
                    >
                      {testimonials[currentIndex].name}
                    </motion.div>
                    <div className="text-gray-600">
                      {testimonials[currentIndex].position}
                    </div>
                    {/* Removed company name */}
                    {/* <motion.div
                      className="text-primary-600 font-medium"
                      whileHover={{ scale: 1.05 }}
                    >
                      {testimonials[currentIndex].company}
                    </motion.div> */}
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Enhanced Dots Navigation */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className={`h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-primary-600 w-8' : 'bg-gray-300 w-3'
                }`}
                animate={index === currentIndex ? {
                  boxShadow: [
                    "0 0 0 0 rgba(0, 82, 204, 0.4)",
                    "0 0 0 8px rgba(0, 82, 204, 0)",
                  ]
                } : {}}
                transition={{
                  boxShadow: {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
